import { useState } from 'react';
import { useFileList } from '../../../hooks/use-file-list';
import {
  useGetBugReportById,
  usePostBugReportMessage,
} from '../../../services/redux/api/bugReportApi';
import { useGetMyDetails } from '../../../services/redux/api/usersApi';
import { useFormik } from 'formik';
import { getReportFormValidation } from './FormikData';
import { useTranslation } from 'react-i18next';
import { BugReportStatus } from '../../../shared/constants';
import { useGetFilePaths } from '../../../services/redux/api/appConfigApi';
import { IAppFile } from '../../../types/interfaces/app-file';
import {
  deleteFilesFromFirebase,
  uploadFileAndCreateAppFile,
} from '../../../utils/firebase-storage-utils';

export function useBugReport(reportId: string) {
  const { data: report } = useGetBugReportById(reportId);
  const [postMessage] = usePostBugReportMessage();
  const { data: user } = useGetMyDetails();
  const {
    filesToUpload: newPostFilesToUpload,
    onDeleteFile: onDeleteNewPostFile,
    onUploadFiles: onUploadNewPostFiles,
    resetFilesState: resetNewPostFilesState,
  } = useFileList([]);
  const { t } = useTranslation();
  const { data: filePaths } = useGetFilePaths();
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getReportFormValidation(t),
    initialValues: {
      message: '',
      status: BugReportStatus.responded,
    },
    onSubmit: onSendMessage,
  });

  async function onSendMessage() {
    if (!filePaths) {
      return;
    }

    const uploadedStoragePaths: string[] = [];

    try {
      setIsLoading(true);

      const uploadedFiles: IAppFile[] = await Promise.all(
        newPostFilesToUpload.map(async (file) => {
          const uploadedFile = await uploadFileAndCreateAppFile(
            file,
            filePaths.bugReportMessages,
          );

          uploadedStoragePaths.push(uploadedFile.storageLink);
          if (uploadedFile.previewStorageLink) {
            uploadedStoragePaths.push(uploadedFile.previewStorageLink);
          }
          return uploadedFile;
        }),
      );

      await postMessage({
        reportId: report.id,
        body: {
          message: {
            userId: user?.id,
            reportId,
            message: formik.values.message,
          },
          status: formik.values.status,
          filesToUpload: uploadedFiles as any,
        },
      }).unwrap();

      formik.resetForm();
      resetNewPostFilesState();
    } catch (error) {
      console.error('Error uploading files or sending message:', error);
      if (uploadedStoragePaths.length > 0) {
        await deleteFilesFromFirebase(uploadedStoragePaths);
      }
    } finally {
      setIsLoading(false);
    }
  }

  return {
    formik,
    report,
    newPostFilesToUpload,
    isLoading,
    onDeleteNewPostFile,
    onUploadNewPostFiles,
  };
}
